import QuickViewWorkOrder from "components/QuickViewWorkOrder/QuickViewWorkOrder";
import Spinner from "components/Spinner";
import { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import authHeader from "services/auth-header";
import API from "services/axios";

export default function JobBoardFullViewData({
  filterBy,
  API_CALL,
  filterText,
  selectedLocation,
  woStatus,
  dateFrom,
  dateTo,
  sortBy,
  sortDir,
}) {
  const [searchParams, setSearchParams] = useSearchParams();

  /*   All States
   ********************************************* */
  const [searchText, setSearchText] = useState("");
  const [allData, setAllData] = useState([]);
  const [displayData, setDisplayData] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [showProductsModal, setShowProductsModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  /*   All Functions
   ********************************************* */

  const updateSearchParams = (key, value) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);

      if (value) {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }

      return newParams.toString();
    });
  };

  const fetchAll = async () => {
    // The date range and sort belong in this key too: without them,
    // reopening the full view after changing the filter is treated as the
    // same request and serves the previous, wrongly-filtered rows.
    const key = `${API_CALL || ""}|${filterBy?.productTypeId || ""}|${
      filterText || ""
    }|${woStatus || ""}|${selectedLocation || ""}|${dateFrom || ""}|${
      dateTo || ""
    }|${sortBy || ""}|${sortDir || ""}`;

    // avoid duplicate fetches for same params
    if (lastFetchKey.current === key && allData?.length) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      lastFetchKey.current = key;

      const searchQuery = filterText?.trim();
      const { data } = await API.post(
        `${API_CALL}?page=1&limit=10000`,
        {
          query: searchQuery || undefined,
          productTypeId: filterBy?.productTypeId || undefined,
          locationId: selectedLocation || undefined,
          // Required by the Pending endpoint (it 406s without it); the other
          // job-board endpoints ignore it.
          WOStatus: woStatus || undefined,
          dateFrom: dateFrom || undefined,
          dateTo: dateTo || undefined,
          sortBy: sortBy || undefined,
          sortDir: sortBy ? sortDir : undefined,
        },
        { headers: authHeader() },
      );

      if (data?.success) {
        setAllData(data.data || []);
        setDisplayData(data.data || []);
      } else if (data?.type === "success") {
        setAllData(data.result || []);
        setDisplayData(data.result || []);
      } else {
        setAllData([]);
        setDisplayData([]);
        setError(data?.message || null);
      }
    } catch (err) {
      setAllData([]);
      setDisplayData([]);
      setError(err.message || "Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  /*   All UseEffects
   ********************************************* */
  const lastFetchKey = useRef(null);

  useEffect(() => {
    let timeout = setTimeout(() => {
      fetchAll();
    }, 50);
    return () => clearTimeout(timeout);

    // intentionally exclude WOData to avoid re-fetch when parent updates paginated data
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    API_CALL,
    filterBy?.productTypeId,
    filterText,
    selectedLocation,
    woStatus,
  ]);

  // no fallback to WOData; component uses API only

  useEffect(() => {
    if (searchText) {
      const filteredItems = (allData || []).filter((item) => {
        return (
          item?.id?.toString().includes(searchText) ||
          item?.jobName?.toLowerCase().includes(searchText.toLowerCase()) ||
          item?.customerName?.toLowerCase().includes(searchText.toLowerCase())
        );
      });
      setDisplayData(filteredItems);
    } else {
      setDisplayData(allData || []);
    }
  }, [searchText, allData]);

  return (
    <div className="" style={{ height: "78vh" }}>
      {/*   View ALL Modal
       ********************************************* */}
      <div className="topHeader bg-primary text-white custom-border-radius d-flex justify-content-between align-items-center">
        {loading && <Spinner />}
        <div>{filterBy?.productTypes}</div>
        <div className="d-flex justify-content-center align-items-center">
          <div className="d-flex justify-content-center align-items-center">
            <input
              type="search"
              placeholder="Search by job name..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="form-control me-3"
            />
          </div>

          <div className="mx-3">
            {loading ? (
              <span className="small text-muted">Loading...</span>
            ) : (
              <span>{displayData?.length}</span>
            )}
          </div>
        </div>
      </div>
      <div className="bodyHeader row">
        <div className="col-1">W-ID</div>
        <div className="col-5">Job Name</div>
        <div className="col-4">Customer Name</div>
        {/* <div className="col-2 text-end">Quantity</div> */}
      </div>
      <div className="bodyContent-2 overflow-auto ">
        {displayData?.map((item, index) => (
          <div
            onDoubleClick={() => {
              setShowProductsModal(true);
              updateSearchParams("selected-work-order", item?.woId);
              setSelectedItem(item);
            }}
            key={index}
            className="row item"
          >
            <div className="col-1">W-{item?.woId}</div>
            <div className=" col-5 text-nowrap">
              {item?.jobName?.slice(0, 30)}
              {item?.jobName?.length >= 30 ? "..." : ""}
            </div>
            <div className=" col-4">
              {item?.customerName?.slice(0, 30)}
              {item?.customerName?.length >= 30 ? "..." : ""}
            </div>
            {/* <div className="col-2 text-end">{item?.totalQuantity}</div> */}
          </div>
        ))}
        {!loading && !displayData?.length && (
          <div className="text-center py-2 text-muted">No records found</div>
        )}
      </div>

      <QuickViewWorkOrder
        filterBy={filterBy}
        // selectedId={selectedItem?.woId}
        updateSearchParams={updateSearchParams}
        show={showProductsModal}
        setShow={setShowProductsModal}
        workOrdersList={displayData}
        // refreshFunc={getWOByproductType}
      />
    </div>
  );
}
